import { lazy, Suspense, useEffect, useState } from "react";

//DEPENDENCIES
import { createBrowserRouter, RouterProvider } from "react-router-dom";
// import { hotjar } from "react-hotjar";

//UTILS
import { appRoutesList } from "./routes/appRoutesList";

//UI COMPONENTS
import ErrorPage from "./pages/ErrorPage/ErrorPage";
import Layout from "./components/Layout/Layout";
import ProtectedRoute from "./components/ProtectedRoute/ProtectedRoute";

// CAPACITOR
import { SplashScreen } from "@capacitor/splash-screen";
import { EdgeToEdge } from "@capawesome/capacitor-android-edge-to-edge-support";
import { StatusBar, Style } from "@capacitor/status-bar";
import { Capacitor } from "@capacitor/core";
import { App as CapApp } from "@capacitor/app";
import { Device } from "@capacitor/device";
import { setBackForwardNavigationGestures } from "capacitor-plugin-ios-webview-configurator";
import { PrivacyScreen } from "@capacitor/privacy-screen";
import { PRIVACY_SCREEN_CONFIG } from "./constants";
import * as Sentry from "@sentry/react";

import { colors } from "./styles/_variables";
import { MockDataProvider } from "./hooks/useMockData";
import { useAppDispatch } from "./store/hooks";
import { setAppInForeground } from "./store/slices/appStateSlice";
import { useOtaUpdates } from "./hooks/useOtaUpdates";
import LoadingSpinner from "./components/common/LoadingSpinner";
import AppVersionGate from "./components/AppVersionGate/AppVersionGate";
import { ENABLE_APP_STATE_LOGS } from "./constants/logging";

const appLog = (...args: unknown[]) => {
  if (!ENABLE_APP_STATE_LOGS) return;

  console.log(...args);
};

import styles from "./pages/LoginPage/LoginPage.module.scss";

// Threshold for showing the "Preparando la aplicación..." label during OTA
// boot. Matches the login flow's long-request threshold to keep UX consistent.
// If the OTA finishes before this, the spinner stays bare without any text.
const OTA_LONG_BOOT_THRESHOLD_MS = 6000;

const LoginPage = lazy(() => import("./pages/LoginPage/LoginPage"));

const router = createBrowserRouter([
  {
    path: "/",
    element: (
      <MockDataProvider>
        <ProtectedRoute>
          <Layout />
        </ProtectedRoute>
      </MockDataProvider>
    ),
    errorElement: <ErrorPage />,
    children: appRoutesList.map(({ element, route }) => {
      return {
        path: route,
        element: (
          <Sentry.ErrorBoundary fallback={<ErrorPage />}>
            {element}
          </Sentry.ErrorBoundary>
        ),
        errorElement: <ErrorPage />,
      };
    }),
  },
  {
    path: "/login",
    element: (
      <Suspense fallback={null}>
        <LoginPage />
      </Suspense>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: "/error",
    element: <ErrorPage />,
  },
  {
    path: "/error-role",
    element: <ErrorPage />,
  },
]);

const showSplash = async () => {
  await SplashScreen.show({
    autoHide: true,
  });
};

const configureAndroidSystemBars = async () => {
  if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== "android")
    return;

  try {
    const device = await Device.getInfo();
    const androidMajor = Number.parseInt(device.osVersion, 10);
    const isAndroid15OrAbove =
      Number.isFinite(androidMajor) && androidMajor >= 15;

    if (isAndroid15OrAbove) {
      await EdgeToEdge.enable();
      await EdgeToEdge.setBackgroundColor({ color: colors.default.DEFAULT });
      await StatusBar.setOverlaysWebView({ overlay: true });
    } else {
      await EdgeToEdge.disable();
      await StatusBar.setOverlaysWebView({ overlay: false });
      await StatusBar.setBackgroundColor({ color: colors.default.DEFAULT });
      // Force the CSS variables to 0
      // since there is no actual overlap to compensate for.
      document.documentElement.style.setProperty("--safe-area-top", "0px");
      document.documentElement.style.setProperty("--safe-area-bottom", "0px");
    }

    await StatusBar.setStyle({ style: Style.Light });
  } catch (error) {
    console.warn("Failed to configure Android system bars", error);
  }
};

function App() {
  const dispatch = useAppDispatch();
  const { otaReady } = useOtaUpdates();

  const { spinnerContainer } = styles;

  // Only show the "Preparando la aplicación..." label when the OTA boot
  // takes longer than OTA_LONG_BOOT_THRESHOLD_MS. On fast boots the spinner
  // stays bare and the user never sees any text.
  const [showOtaLongBootMessage, setShowOtaLongBootMessage] = useState(false);

  useEffect(() => {
    appLog("[App] mount", {
      isNative: Capacitor.isNativePlatform(),
      platform: Capacitor.getPlatform(),
      visibilityState: document.visibilityState,
    });
  }, []);

  useEffect(() => {
    appLog("[App] otaReady changed", { otaReady });
  }, [otaReady]);

  useEffect(() => {
    if (otaReady) {
      setShowOtaLongBootMessage(false);
      return;
    }
    const timer = window.setTimeout(() => {
      setShowOtaLongBootMessage(true);
    }, OTA_LONG_BOOT_THRESHOLD_MS);
    return () => window.clearTimeout(timer);
  }, [otaReady]);

  // useEffect(() => {
  //   if (import.meta.env.VITE_ENVIRONMENT === "prod") {
  //     hotjar.initialize({
  //       id: Number(import.meta.env.VITE_HOTJAR_ID),
  //       sv: Number(import.meta.env.VITE_HOTJAR_SV),
  //     });
  //   }
  // }, []);

  useEffect(() => {
    if (Capacitor.getPlatform() === "ios") {
      setBackForwardNavigationGestures(true);
    }
  }, []);

  // Pre-warm iOS keyboard to avoid lag on first open
  useEffect(() => {
    if (Capacitor.getPlatform() !== "ios") return;
    const input = document.createElement("input");
    input.style.cssText =
      "position:fixed;opacity:0;pointer-events:none;top:-100px;";
    document.body.appendChild(input);
    input.focus();
    setTimeout(() => {
      input.blur();
      document.body.removeChild(input);
    }, 50);
  }, []);

  useEffect(() => {
    void configureAndroidSystemBars();
  }, []);

  useEffect(() => {
    showSplash();
    return () => {
      SplashScreen.hide();
    };
  }, []);

  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      PrivacyScreen.enable(PRIVACY_SCREEN_CONFIG);
      return () => {
        PrivacyScreen.disable();
      };
    }
  }, []);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    const t0 = Date.now();
    const log = (msg: string, extra?: unknown) => {
      if (!ENABLE_APP_STATE_LOGS) return;
      const elapsed = Date.now() - t0;
      if (extra !== undefined) {
        console.log(`[AppState] +${elapsed}ms ${msg}`, extra);
      } else {
        console.log(`[AppState] +${elapsed}ms ${msg}`);
      }
    };

    log("effect mounted");

    const updateForegroundState = (isActive: boolean, source: string) => {
      log(`dispatch setAppInForeground(${isActive}) from ${source}`);
      dispatch(setAppInForeground(isActive));
    };

    // Release any orphaned body scroll lock left behind when the app was
    // backgrounded mid-transition (e.g. modal closing animation interrupted).
    // Skip if a dialog is still open so we don't break legitimate locks.
    const releaseStuckScrollLock = () => {
      if (document.querySelector('[role="dialog"][aria-modal="true"]')) return;
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
      document.body.removeAttribute("data-overflow");
    };

    let appStateListener: { remove: () => void } | undefined;
    let resumeListener: { remove: () => void } | undefined;
    let pauseListener: { remove: () => void } | undefined;

    void (async () => {
      try {
        log("calling CapApp.getState()");
        const state = await CapApp.getState();
        log("CapApp.getState() returned", { isActive: state.isActive });
        // Only upgrade to true. iOS sometimes reports isActive=false during
        // cold start while still in foreground (race between JS and the OS
        // launch transition). The slice initial state is true and the
        // pause/appStateChange events reliably flip it to false on real
        // background, so we never need to dispatch false from here.
        if (state.isActive) {
          updateForegroundState(true, "getState");
        } else {
          log(
            "CapApp.getState() returned false on cold start — ignoring (slice initial state is true, events will correct if needed)",
          );
          try {
            Sentry.addBreadcrumb({
              category: "auth.appstate",
              message:
                "getState returned isActive=false on cold start, ignored",
              level: "warning",
            });
          } catch {
            // Sentry must never block boot.
          }
        }
      } catch (error) {
        log("CapApp.getState() FAILED", error);
        console.warn("Failed to get app state", error);
      }

      log("registering appStateChange listener");
      appStateListener = await CapApp.addListener(
        "appStateChange",
        ({ isActive }) => {
          log("event appStateChange", { isActive });
          updateForegroundState(isActive, "appStateChange");
          if (isActive) releaseStuckScrollLock();
        },
      );
      log("appStateChange listener registered");

      resumeListener = await CapApp.addListener("resume", () => {
        log("event resume");
        updateForegroundState(true, "resume");
        void configureAndroidSystemBars();
        releaseStuckScrollLock();
      });
      log("resume listener registered");

      pauseListener = await CapApp.addListener("pause", () => {
        log("event pause");
        updateForegroundState(false, "pause");
      });
      log("pause listener registered (all listeners ready)");
    })();

    // Diagnostic watchdog: poll CapApp.getState() and visibilityState
    // periodically so we can tell if iOS truly considers the app backgrounded
    // or if we just missed the transition event. Bounded to first 30s after
    // mount to avoid noise.
    const watchdogIntervals = [1000, 3000, 6000, 12_000, 20_000, 30_000];
    const watchdogTimers = watchdogIntervals.map((delay) =>
      window.setTimeout(async () => {
        try {
          const current = await CapApp.getState();
          log(`watchdog poll @${delay}ms`, {
            isActive: current.isActive,
            visibilityState: document.visibilityState,
            documentHidden: document.hidden,
          });
        } catch (e) {
          log(`watchdog poll @${delay}ms FAILED`, e);
        }
      }, delay),
    );

    // Browser-level visibility signal (independent of Capacitor's AppState)
    const onVisibility = () => {
      log("event visibilitychange", {
        visibilityState: document.visibilityState,
        documentHidden: document.hidden,
      });
    };
    document.addEventListener("visibilitychange", onVisibility);
    log("visibilitychange listener attached", {
      initialVisibilityState: document.visibilityState,
    });

    return () => {
      appStateListener?.remove?.();
      resumeListener?.remove?.();
      pauseListener?.remove?.();
      for (const id of watchdogTimers) window.clearTimeout(id);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [dispatch]);

  if (!otaReady)
    return (
      <LoadingSpinner
        className={spinnerContainer}
        size="lg"
        label={
          showOtaLongBootMessage ? "Preparando la aplicación..." : undefined
        }
      />
    );

  return (
    <Sentry.ErrorBoundary fallback={<div />}>
      <AppVersionGate>
        <RouterProvider router={router} />
      </AppVersionGate>
    </Sentry.ErrorBoundary>
  );
}

export default App;
